import {
    ChangeEvent,
    Dispatch,
    SetStateAction,
    useEffect,
    useState
} from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { Field } from "@atlaskit/form";
import CheckCircleOutlineIcon from "@atlaskit/icon/glyph/check-circle-outline";
import ErrorIcon from "@atlaskit/icon/glyph/error";
import TextField from "@atlaskit/textfield";
import { G300, N0, N500, R400 } from "@atlaskit/theme/colors";

import { emailIsValid, fontSize } from "@/components/signup/utils";
import { usePaidJswProductTourContext } from "@/templates/paid-landing/src/hooks/use-paid-jsw-product-tour-context";

import { useSignupFormContext } from "../../hooks/signup-form-context";

export const InputEmail = ({
    hasRebrandedSignupFormStyles,
    setIsEmailValid,
    setTypeAheadEmail,
    submitting,
    workCallout
}: {
    readonly hasRebrandedSignupFormStyles?: boolean;
    readonly setIsEmailValid: Dispatch<SetStateAction<boolean>>;
    readonly setTypeAheadEmail: Dispatch<SetStateAction<string>>;
    readonly submitting: boolean;
    readonly workCallout?: boolean;
}) => {
    const { isAnimatedHero } = usePaidJswProductTourContext();

    const { typeAheadEmail, isReadyForInvalid, setIsReadyForInvalid } =
        useSignupFormContext();
    const [email, setEmail] = useState(typeAheadEmail);
    const [isValid, setValid] = useState<boolean>();
    const intl = useIntl();
    const errorMessageText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.invalidEmail",
        defaultMessage: "Please enter a valid email address."
    });
    const workCalloutText = workCallout
        ? intl.formatMessage({
              id: "BxpUiSignup.SignupForm.workCallout",
              defaultMessage:
                  "Find teammates, plus keep work and life separate by using your work email."
          })
        : "";

    const labelText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.workEmail",
        defaultMessage: "Work email"
    });
    const placeholderText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.emailPlaceholder",
        defaultMessage: "you@company.com"
    });
    const paidPlaceholderText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.emailPlaceholderPaidExperiment",
        defaultMessage: "Enter work email"
    });

    useEffect(() => {
        if (submitting && !isReadyForInvalid) {
            setIsReadyForInvalid(true);
        }
    }, [submitting, isReadyForInvalid]);

    useEffect(() => {
        const isValid = emailIsValid(email);

        // wait to show invalid state if the user is not finished typing
        if (!isValid && !isReadyForInvalid) {
            setValid(undefined);
            return;
        }

        setValid(isValid);
        setIsEmailValid(isValid);
    }, [email, setIsEmailValid, isReadyForInvalid]);

    return (
        <div css={fieldCss}>
            <Field
                aria-required={true}
                isRequired
                label={
                    <span
                        className="workEmail"
                        css={[labelCss, isAnimatedHero && noTopPadding]}
                    >
                        {labelText}
                    </span>
                }
                name="email"
            >
                {({ fieldProps }) => (
                    <div
                        css={[
                            isValid === true && inputValidCss,
                            isValid === false && inputInvalidCss
                        ]}
                    >
                        <TextField
                            css={
                                hasRebrandedSignupFormStyles && textFieldStyles
                            }
                            {...fieldProps}
                            autoComplete="on"
                            elemAfterInput={<InputIcon valid={isValid} />}
                            onBlur={() => setIsReadyForInvalid(true)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setEmail(e.target.value);
                                setTypeAheadEmail(e.target.value);
                                fieldProps.onChange(e);
                            }}
                            placeholder={
                                isAnimatedHero
                                    ? paidPlaceholderText
                                    : placeholderText
                            }
                            testId="email_field"
                            type="email"
                            value={email}
                        />
                        {isValid === false ? (
                            <div
                                className="errorMessageText"
                                css={errorMessageCss}
                                aria-live="assertive"
                                role="alert"
                            >
                                {errorMessageText}
                            </div>
                        ) : workCallout ? (
                            <div css={helpTextCss}>{workCalloutText}</div>
                        ) : null}
                    </div>
                )}
            </Field>
        </div>
    );
};

const InputIcon = ({ valid }: { valid: boolean | undefined }) =>
    typeof valid === "undefined" ? null : valid ? (
        <div css={iconValidCss}>
            <CheckCircleOutlineIcon label="" size="medium" />
        </div>
    ) : (
        <div css={iconInvalidCss}>
            <ErrorIcon label="" size="medium" />
        </div>
    );

const errorMessageCss = css({
    color: R400,
    fontSize: fontSize.xs,
    lineHeight: 1.5,
    marginBlockStart: 6
});

const helpTextCss = css({
    fontSize: fontSize.xs,
    lineHeight: 1.5,
    marginBlockStart: 6,
    color: N500
});

const textFieldStyles = css({
    borderRadius: "40px !important",
    paddingInlineStart: "8px",
    paddingInlineEnd: "8px"
});

const fieldCss = css({
    "marginBlockEnd": 16,
    // Note: Existing tech debt that should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "input": {
        backgroundColor: `${N0} !important`,
        fontSize: fontSize.md
    },
    "&:first-of-type": {
        marginBlockStart: 0
    },
    // Note: Existing tech debt that should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "span:nth-of-type(2)": {
        display: "none"
    }
});

const iconWrapperStyle = {
    alignItems: "center",
    display: "flex",
    fill: N0,
    fontSize: fontSize.md,
    paddingInlineEnd: 8
};

const iconValidCss = css({
    ...iconWrapperStyle,
    color: G300
});

const iconInvalidCss = css({
    ...iconWrapperStyle,
    color: R400
});

const inputInvalidCss = css({
    // Note: Existing tech debt that should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> div": {
        borderColor: `${R400} !important`
    }
});

const inputValidCss = css({
    // Note: Existing tech debt that should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> div": {
        borderColor: `${G300} !important`
    }
});

const labelCss = css({
    fontWeight: 500
});

const noTopPadding = css({
    paddingBlockStart: "0px"
});
